//Pagina comun que usa para integrar cada tarjeta-informacion-curso en la pagina de base BasePresentacionCursos

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import BasePresentacionCursos from "../../components/BasePresentacionCursos";
import ConsentCheckbox from "../../components/ConsentCheckbox";
import { API_URL } from "../../config";
import { TERMS_VERSION, PRIVACY_VERSION } from "../../utils/consentVersions";
import "./CursoDetalle.css";
import { FALLBACK_IMAGE_SRC } from "../../utils/placeholderImage";
import CourseHeroImage from "../../components/CourseHeroImage";
import CourseDescriptionContent from "../../components/CourseDescriptionContent";
import { COURSE_ALREADY_OWNED_CODE, isCourseOwnedByUser } from "../../utils/ownedCourseIds";
import { useOwnedCourseIds } from "../../hooks/useOwnedCourseIds";
import { getMessage } from "../../data/locales";

const CursoDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [curso, setCurso] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [pagoEnProceso, setPagoEnProceso] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const { ownedCourseIds, ownershipReady } = useOwnedCourseIds();

  useEffect(() => {
    const obtenerCurso = async () => {
      try {
        const res = await fetch(`${API_URL}/cursos/${id}`);
        const data = await res.json();
        setCurso(data);
      } catch (error) {
        console.error("Error al cargar el curso:", error);
      } finally {
        setCargando(false);
      }
    };
    obtenerCurso();
  }, [id]);

  const manejarPago = async () => {
    if (!curso || !curso.activo || !ownershipReady) return;
    if (isCourseOwnedByUser(curso._id, ownedCourseIds)) {
      alert(getMessage("checkout.alreadyOwnedCourse"));
      navigate(`/usuario/curso/${curso._id}`);
      return;
    }
    if (!consentAccepted) {
      alert("Debes aceptar los términos y condiciones y la política de privacidad.");
      return;
    }

    try {
      setPagoEnProceso(true);
      const res = await fetch(`${API_URL}/api/pedidos`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          items: [{ cursoId: curso._id, qty: 1 }],
          consentAccepted,
          termsVersion: TERMS_VERSION,
          policyVersion: PRIVACY_VERSION,
        })
      });

      if (res.status === 401) {
        navigate("/login");
        return;
      }

      const data = await res.json();

      if (res.ok) {
        navigate(`/checkout?orderId=${data.orderId}`);
      } else if (data?.code === COURSE_ALREADY_OWNED_CODE) {
        alert(getMessage("checkout.alreadyOwnedCourse"));
        navigate("/usuario/campus");
      } else {
        alert(`❌ Error al iniciar el pago: ${data.error || "Intenta más tarde."}`);
      }
    } catch (error) {
      console.error("Error al procesar el pago:", error);
      alert("❌ Error al procesar el pago.");
    } finally {
      setPagoEnProceso(false);
    }
  };

  if (cargando) {
    return (
      <BasePresentacionCursos>
        <div className="curso-detalle-container">
          <div className="spinner"></div> {/* 👈 Nuevo Spinner */}
        </div>
      </BasePresentacionCursos>
    );
  }

  if (!curso) {
    return (
      <BasePresentacionCursos>
        <div className="curso-detalle-container">Curso no encontrado.</div>
      </BasePresentacionCursos>
    );
  }

  return (
    <BasePresentacionCursos>
      <div className="curso-detalle-container">
        <div className="curso-detalle-card">
          <CourseHeroImage
            src={curso.imagen || FALLBACK_IMAGE_SRC}
            alt={curso.titulo}
            className="curso-detalle-imagen"
          />
          {!curso.activo && (
            <div className="curso-no-disponible">
              🚫 Curso actualmente no disponible (próxima apertura)
            </div>
          )}

          <h1 className="curso-detalle-titulo">{curso.titulo}</h1>
          <CourseDescriptionContent
            html={curso.descripcion}
            className="curso-detalle-descripcion"
          />

          <p className="curso-detalle-precio">
            Precio: {curso.precioFinal} €
          </p>

          {curso.duracion && (
            <p className="curso-detalle-duracion">
              ⏳ Duración: {curso.duracion}
            </p>
          )}

          {curso.fechaApertura && (
            <p className="curso-detalle-fecha">
              📅 Apertura: {new Date(curso.fechaApertura).toLocaleDateString()}
            </p>
          )}

          <ConsentCheckbox
            checked={consentAccepted}
            onChange={setConsentAccepted}
          />
          <div className="curso-detalle-botones">
            {!ownershipReady ? (
              <button type="button" className="boton-acceder" disabled>
                {getMessage("checkout.checkingAccess")}
              </button>
            ) : isCourseOwnedByUser(curso._id, ownedCourseIds) ? (
              <button
                type="button"
                className="boton-acceder"
                onClick={() => navigate(`/usuario/curso/${curso._id}`)}
              >
                {getMessage("checkout.goToCampus")}
              </button>
            ) : (
            <button
              className="boton-acceder"
              disabled={!curso.activo || pagoEnProceso || !consentAccepted}
              onClick={manejarPago}
            >
              {curso.activo
                ? pagoEnProceso
                  ? "Procesando pago..."
                  : "💳 Pagar y Acceder"
                : "Curso no disponible"}
            </button>
            )}
          </div>
        </div>
      </div>
    </BasePresentacionCursos>
  );
};

export default CursoDetalle;
